import React from 'react';
import { TrendingUp, TrendingDown, BarChart3, PieChart, Activity } from 'lucide-react';

/**
 * Statistical Analysis Component for Comparison Module
 * Provides advanced statistical insights for scenario comparison
 */

const fc = (v) => typeof v === 'number' ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';
const fp = (v) => typeof v === 'number' ? `${v.toFixed(2)}%` : '0%';

// Calculate statistical metrics
const calculateStats = (values) => {
  if (!values || values.length === 0) return null;
  
  const sorted = [...values].sort((a, b) => a - b);
  const n = values.length;
  const mean = values.reduce((a, b) => a + b, 0) / n;
  
  // Median
  const median = n % 2 === 0
    ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2
    : sorted[Math.floor(n / 2)];
  
  // Standard Deviation
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / n;
  const stdDev = Math.sqrt(variance);
  
  // Coefficient of Variation (CV)
  const cv = mean !== 0 ? (stdDev / Math.abs(mean)) * 100 : 0;
  
  // Range
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  
  // Quartiles
  const q1 = sorted[Math.floor(n * 0.25)];
  const q3 = sorted[Math.floor(n * 0.75)];
  const iqr = q3 - q1;
  
  return {
    mean,
    median,
    stdDev,
    cv,
    min,
    max,
    range,
    q1,
    q3,
    iqr,
    count: n
  };
};

// Calculate correlation between two arrays
const calculateCorrelation = (x, y) => {
  if (!x || !y || x.length !== y.length || x.length < 2) return null;
  
  const n = x.length;
  const meanX = x.reduce((a, b) => a + b, 0) / n;
  const meanY = y.reduce((a, b) => a + b, 0) / n;
  
  let numerator = 0;
  let denomX = 0;
  let denomY = 0;
  
  for (let i = 0; i < n; i++) {
    const diffX = x[i] - meanX;
    const diffY = y[i] - meanY;
    numerator += diffX * diffY;
    denomX += diffX * diffX;
    denomY += diffY * diffY;
  }
  
  if (denomX === 0 || denomY === 0) return 0;
  
  return numerator / Math.sqrt(denomX * denomY);
};

// Identify outliers using IQR method
const identifyOutliers = (values, q1, q3, iqr) => {
  const lowerBound = q1 - 1.5 * iqr;
  const upperBound = q3 + 1.5 * iqr;
  return values.map((v, i) => ({
    index: i,
    value: v,
    isOutlier: v < lowerBound || v > upperBound,
    type: v < lowerBound ? 'low' : v > upperBound ? 'high' : 'normal'
  }));
};

// Calculate risk-adjusted return (Sharpe-like ratio)
const calculateRiskAdjustedReturn = (returns, stdDev) => {
  if (stdDev === 0) return 0;
  const meanReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
  return meanReturn / stdDev;
};

export default function StatisticalAnalysis({ scenarios }) {
  if (!scenarios || scenarios.length < 2) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <p className="text-sm text-slate-500">Need at least 2 scenarios for statistical analysis</p>
      </div>
    );
  }

  // Extract metrics
  const grossRevenues = scenarios.map(s => s.gross_revenue || 0);
  const netRevenues = scenarios.map(s => s.net_revenue || 0);
  const gpmValues = scenarios.map(s => s.gpm || 0);
  const netMargins = scenarios.map(s => s.net_margin || 0);
  const merchantFees = scenarios.map(s => s.merchant_fees || 0);

  // Calculate statistics
  const grossStats = calculateStats(grossRevenues);
  const netStats = calculateStats(netRevenues);
  const gpmStats = calculateStats(gpmValues);
  const marginStats = calculateStats(netMargins);

  // Calculate correlations
  const grossNetCorr = calculateCorrelation(grossRevenues, netRevenues);
  const grossGpmCorr = calculateCorrelation(grossRevenues, gpmValues);
  const netMarginCorr = calculateCorrelation(netRevenues, netMargins);

  // Identify outliers
  const grossOutliers = identifyOutliers(grossRevenues, grossStats.q1, grossStats.q3, grossStats.iqr);
  const outlierScenarios = grossOutliers.filter(o => o.isOutlier);

  // Calculate risk-adjusted metrics
  const riskAdjustedGross = calculateRiskAdjustedReturn(grossRevenues, grossStats.stdDev);
  const riskAdjustedNet = calculateRiskAdjustedReturn(netRevenues, netStats.stdDev);

  // Performance categorization
  const categorizePerformance = (value, mean, stdDev) => {
    if (value > mean + stdDev) return { label: 'High Performer', color: 'text-green-600', bg: 'bg-green-50' };
    if (value < mean - stdDev) return { label: 'Low Performer', color: 'text-red-600', bg: 'bg-red-50' };
    return { label: 'Average', color: 'text-slate-600', bg: 'bg-slate-50' };
  };

  const scenariosWithCategories = scenarios.map((s, i) => ({
    ...s,
    index: i,
    grossCategory: categorizePerformance(s.gross_revenue, grossStats.mean, grossStats.stdDev),
    netCategory: categorizePerformance(s.net_revenue, netStats.mean, netStats.stdDev),
    gpmCategory: categorizePerformance(s.gpm, gpmStats.mean, gpmStats.stdDev),
  }));

  const highPerformers = scenariosWithCategories.filter(s => s.grossCategory.label === 'High Performer' && s.netCategory.label === 'High Performer');
  const lowPerformers = scenariosWithCategories.filter(s => s.grossCategory.label === 'Low Performer' || s.netCategory.label === 'Low Performer');

  return (
    <div className="space-y-6">
      {/* Statistical Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          title="Gross Revenue"
          mean={grossStats.mean}
          stdDev={grossStats.stdDev}
          cv={grossStats.cv}
          range={[grossStats.min, grossStats.max]}
          format="currency"
        />
        <StatCard
          title="Net Revenue"
          mean={netStats.mean}
          stdDev={netStats.stdDev}
          cv={netStats.cv}
          range={[netStats.min, netStats.max]}
          format="currency"
        />
        <StatCard
          title="GPM"
          mean={gpmStats.mean}
          stdDev={gpmStats.stdDev}
          cv={gpmStats.cv}
          range={[gpmStats.min, gpmStats.max]}
          format="percent"
        />
        <StatCard
          title="Net Margin"
          mean={marginStats.mean}
          stdDev={marginStats.stdDev}
          cv={marginStats.cv}
          range={[marginStats.min, marginStats.max]}
          format="percent"
        />
      </div>

      {/* Advanced Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Correlation Analysis */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-bold text-slate-900">Correlation Analysis</h3>
          </div>
          <div className="space-y-3">
            <CorrelationBar
              label="Gross ↔ Net Revenue"
              value={grossNetCorr}
              description="How gross and net revenue move together"
            />
            <CorrelationBar
              label="Gross Revenue ↔ GPM"
              value={grossGpmCorr}
              description="Revenue size vs margin efficiency"
            />
            <CorrelationBar
              label="Net Revenue ↔ Net Margin"
              value={netMarginCorr}
              description="Net revenue vs profitability"
            />
          </div>
          <div className="mt-4 p-3 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-800">
              <span className="font-semibold">Interpretation:</span> Values near ±1 indicate strong correlation, 
              near 0 indicates weak/no correlation. Positive values mean metrics move together, negative means inverse relationship.
            </p>
          </div>
        </div>

        {/* Risk-Adjusted Performance */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-bold text-slate-900">Risk-Adjusted Returns</h3>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Gross Revenue Ratio</span>
                <span className="text-sm font-bold text-slate-900">{riskAdjustedGross.toFixed(2)}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600"
                  style={{ width: `${Math.min(Math.abs(riskAdjustedGross) * 10, 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Net Revenue Ratio</span>
                <span className="text-sm font-bold text-slate-900">{riskAdjustedNet.toFixed(2)}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600"
                  style={{ width: `${Math.min(Math.abs(riskAdjustedNet) * 10, 100)}%` }}
                />
              </div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-amber-50 rounded-lg">
            <p className="text-xs text-amber-800">
              <span className="font-semibold">Higher is better:</span> Shows return per unit of risk (variability). 
              Higher ratios indicate more consistent performance relative to average.
            </p>
          </div>
        </div>
      </div>

      {/* Performance Clustering */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <PieChart className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-bold text-slate-900">Performance Clustering</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-green-900">High Performers</span>
              <TrendingUp className="w-4 h-4 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-green-700 mb-2">{highPerformers.length}</div>
            {highPerformers.length > 0 && (
              <div className="space-y-1">
                {highPerformers.map(s => (
                  <div key={s.scenario_id} className="text-xs text-green-800 truncate">
                    • {s.municipality}, {s.state} - {s.service_name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-4 bg-slate-50 border-2 border-slate-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-900">Average</span>
              <Activity className="w-4 h-4 text-slate-600" />
            </div>
            <div className="text-2xl font-bold text-slate-700">
              {scenarios.length - highPerformers.length - lowPerformers.length}
            </div>
            <p className="text-xs text-slate-600 mt-2">Within ±1 std deviation</p>
          </div>

          <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-red-900">Low Performers</span>
              <TrendingDown className="w-4 h-4 text-red-600" />
            </div>
            <div className="text-2xl font-bold text-red-700 mb-2">{lowPerformers.length}</div>
            {lowPerformers.length > 0 && (
              <div className="space-y-1">
                {lowPerformers.map(s => (
                  <div key={s.scenario_id} className="text-xs text-red-800 truncate">
                    • {s.municipality}, {s.state} - {s.service_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Outlier Detection */}
      {outlierScenarios.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">⚠️</span>
            <h3 className="text-lg font-bold text-amber-900">Outliers Detected</h3>
          </div>
          <p className="text-sm text-amber-800 mb-3">
            The following scenarios show unusual gross revenue patterns (beyond 1.5× IQR from quartiles):
          </p>
          <div className="space-y-2">
            {outlierScenarios.map(o => {
              const scenario = scenarios[o.index];
              return (
                <div key={o.index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">
                      {scenario.municipality}, {scenario.state} - {scenario.service_name}
                    </p>
                    <p className="text-xs text-slate-600">{fc(o.value)} gross revenue</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    o.type === 'high' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {o.type === 'high' ? 'Above Average' : 'Below Average'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Variability Insights */}
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">📊 Variability Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-indigo-800">
          <div>
            <p className="font-semibold mb-1">Consistency:</p>
            <p>
              {grossStats.cv < 15 && "Your scenarios show low variability (CV < 15%) - very consistent pricing."}
              {grossStats.cv >= 15 && grossStats.cv < 30 && "Moderate variability (CV 15-30%) - reasonably consistent."}
              {grossStats.cv >= 30 && "High variability (CV > 30%) - significant differences between scenarios."}
            </p>
          </div>
          <div>
            <p className="font-semibold mb-1">Recommendation:</p>
            <p>
              {grossStats.cv < 15 && "Scenarios are tightly clustered. Consider diversifying to explore more pricing options."}
              {grossStats.cv >= 15 && grossStats.cv < 30 && "Good balance of consistency and diversity in pricing scenarios."}
              {grossStats.cv >= 30 && "Wide variation detected. Review outliers and focus on mid-range performers for stability."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, mean, stdDev, cv, range, format = 'currency' }) {
  const formatValue = (v) => format === 'currency' ? fc(v) : fp(v);

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">{title}</h4>
      <div className="space-y-2">
        <div>
          <span className="text-xs text-slate-600">Mean:</span>
          <span className="block text-lg font-bold text-slate-900">{formatValue(mean)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-slate-600">Std Dev:</span>
            <span className="block font-semibold text-slate-700">{formatValue(stdDev)}</span>
          </div>
          <div>
            <span className="text-slate-600">CV:</span>
            <span className="block font-semibold text-slate-700">{fp(cv)}</span>
          </div>
        </div>
        <div className="pt-2 border-t border-slate-100">
          <span className="text-xs text-slate-600">Range:</span>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs font-medium text-slate-700">{formatValue(range[0])}</span>
            <span className="text-xs text-slate-400">→</span>
            <span className="text-xs font-medium text-slate-700">{formatValue(range[1])}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Correlation Bar Component
function CorrelationBar({ label, value, description }) {
  const absValue = Math.abs(value);
  const isPositive = value >= 0;
  const strength = absValue > 0.7 ? 'Strong' : absValue > 0.4 ? 'Moderate' : 'Weak';
  const color = absValue > 0.7 ? 'bg-purple-600' : absValue > 0.4 ? 'bg-purple-400' : 'bg-purple-200';

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-xs font-semibold text-slate-900">
          {value.toFixed(3)} ({strength} {isPositive ? '+' : '-'})
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-px h-full bg-slate-300" />
        </div>
        <div
          className={`h-full ${color}`}
          style={{
            width: `${absValue * 100}%`,
            marginLeft: isPositive ? '50%' : `${(1 - absValue) * 50}%`
          }}
        />
      </div>
      <p className="text-xs text-slate-500 mt-1">{description}</p>
    </div>
  );
}
